'use client';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface StatData {
  label: string;
  value: number;
  prefix: string;
  suffix: string;
  icon: string;
  color: string;
  bg: string;
}

function formatNumber(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(0) + 'K';
  return n.toString();
}

function CountUp({ target, prefix, suffix }: { target: number; prefix: string; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 2000;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="stats-counter">
      {prefix}{formatNumber(count)}{suffix}
    </span>
  );
}

export default function StatsBar() {
  const [stats, setStats] = useState<StatData[]>([
    { label: 'Verified Reviewers', value: 0, prefix: '', suffix: '+', icon: 'UsersIcon', color: '#FF5722', bg: '#FFF3EE' },
    { label: 'Active Campaigns', value: 0, prefix: '', suffix: '', icon: 'MegaphoneIcon', color: '#1A237E', bg: '#EEF0FB' },
    { label: 'Reviews Approved', value: 0, prefix: '', suffix: '+', icon: 'CheckBadgeIcon', color: '#10B981', bg: '#ECFDF5' },
    { label: 'Total Paid Out', value: 0, prefix: '₹', suffix: '+', icon: 'BanknotesIcon', color: '#F59E0B', bg: '#FFFBEB' },
  ]);

  useEffect(() => {
    async function fetchStats() {
      const supabase = createClient();
      try {
        const [reviewersRes, campaignsRes, reviewsRes, walletRes] = await Promise.all([
          supabase.from('user_profiles').select('*', { count: 'exact', head: true }).eq('role', 'reviewer'),
          supabase.from('campaigns').select('*', { count: 'exact', head: true }).eq('campaign_status', 'active'),
          supabase.from('reviews').select('*', { count: 'exact', head: true }).eq('review_status', 'approved'),
          supabase.from('wallet_transactions').select('amount').eq('transaction_type', 'credit'),
        ]);

        const totalPaidOut = walletRes.data?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;

        setStats([
          { label: 'Verified Reviewers', value: reviewersRes.count || 0, prefix: '', suffix: '+', icon: 'UsersIcon', color: '#FF5722', bg: '#FFF3EE' },
          { label: 'Active Campaigns', value: campaignsRes.count || 0, prefix: '', suffix: '', icon: 'MegaphoneIcon', color: '#1A237E', bg: '#EEF0FB' },
          { label: 'Reviews Approved', value: reviewsRes.count || 0, prefix: '', suffix: '+', icon: 'CheckBadgeIcon', color: '#10B981', bg: '#ECFDF5' },
          { label: 'Total Paid Out', value: totalPaidOut, prefix: '₹', suffix: '+', icon: 'BanknotesIcon', color: '#F59E0B', bg: '#FFFBEB' },
        ]);
      } catch (err) {
        console.log('Stats fetch error:', err);
      }
    }
    fetchStats();
  }, []);

  return (
    <section className="bg-white border-y border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
          {stats.map((stat) => (
            <div key={stat.label} className="flex flex-col items-center py-10 px-4 gap-3 text-center group">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center mb-1 transition-transform group-hover:scale-110"
                style={{ backgroundColor: stat.bg }}
              >
                <Icon name={stat.icon as 'UsersIcon'} size={22} style={{ color: stat.color }} />
              </div>
              <div className="font-heading font-bold text-2xl text-foreground">
                <CountUp target={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
              </div>
              <div className="text-xs text-muted-foreground font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}